import distance from "@turf/distance";
import { point } from "@turf/helpers";
import { GtfsService } from "./Gtfs/GtfsService.js";

// A constant used in calculations to separate results sorting into categories.
// The length of the equator is used as it is the maximum distance between any two points on Earth.
const PROXIMITY_DIVIDER = 40075; // the length of the equator in km

export class ProximitySortingService {
  /**
   * Sort the given array of features, in place, according to the distance of the feature to the given
   * proximityCoordinates. If no proximityCoordinates are provided the featureCollection will remain unchanged.
   *
   * @param {Feature<Point, any>[]} features FeatureCollection
   * @param {Coordinates} [proximityCoordinates] Coordinates to sort against based on distance from them
   * @returns {Feature<Point, any>[]} The given featureCollection with its features sorted. Note that the
   * features are sorted in place and no copy is made.
   */
  static sort(features, proximityCoordinates) {
    if (proximityCoordinates) {
      const proximityPoint = point(proximityCoordinates);
      features.sort((feature, otherFeature) => {
        // To prioritise the GTFS station in sorting while preserving proximity sorting, we'll use a constant
        //  to create sections in the sorted list. The constant we're using is the maximum distance between any two points.
        // The rationale is that adding or removing a constant value in the distance calculation
        //  will change the result to distinct portions of the list while maintaining the actual distance difference.
        // For example, we have below points we are comparing against proximity [0,0]:
        // - Point A, non-station, distance from proximity at 1km
        // - Point B, station, distance from proximity at 3km
        // - Point C, station, distance from proximity at 2km
        // When comparing the points, the distance calculation becomes...
        // - Points A and B: Calculation is at 0 + PROXIMITY_DIVIDER + 1km - 3km, which sorts them as [B, A]
        // - Points B and C: Calculation is at -PROXIMITY_DIVIDER + PROXIMITY_DIVIDER + 3km - 2km, which sorts them as [C, B]
        // - Points C and A: Calculation is at -PROXIMITY_DIVIDER + 0 + 2km - 1km, which sorts them as [C, A]
        const featureConstant = GtfsService.isFeatureStation(feature) ? -PROXIMITY_DIVIDER : 0;
        const otherFeatureStation = GtfsService.isFeatureStation(otherFeature) ? PROXIMITY_DIVIDER : 0;
        // return <0 to sort a,b; return >0 to sort b,a; return 0 to keep original order
        return (
          featureConstant +
          otherFeatureStation +
          distance(feature, proximityPoint) -
          distance(otherFeature, proximityPoint)
        );
      });
    }
    return features;
  }
}
